import { Card, CardBody } from '@img/ui';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

/**
 * 🔒 سياسة الخصوصية — شركة صورة العقارية
 * متوافقة مع نظام حماية البيانات الشخصية السعودي (PDPL).
 */
export function PrivacyPage() {
  return (
    <div className="bg-gradient-to-b from-navy-soft/30 to-white">
      <SEO
        title="سياسة الخصوصية — شركة صورة العقارية 🔒"
        description="سياسة الخصوصية الكاملة لمنصة شركة صورة العقارية متوافقة مع نظام حماية البيانات الشخصية السعودي (PDPL). كيف نجمع بياناتك، نحميها، نستخدمها، ونضمن سريّتها. العقار بصورة جديدة."
        keywords="سياسة الخصوصية, خصوصية شركة صورة العقارية, حماية البيانات الشخصية, PDPL, نظام حماية البيانات الشخصية, خصوصية img.sa, تشفير البيانات, أمن البيانات, السرّية, ملفات تعريف الارتباط, cookies policy, شركة صورة العقارية, العقار بصورة جديدة"
        canonical="https://img.sa/privacy"
        jsonLd={{
          '@type': 'WebPage',
          name: 'سياسة الخصوصية',
          url: 'https://img.sa/privacy',
          isPartOf: { '@id': 'https://img.sa#website' },
          about: { '@type': 'Thing', name: 'حماية البيانات الشخصية وفق نظام PDPL' },
        }}
      />
      <section className="bg-gradient-to-l from-navy-darker via-navy-dark to-navy text-white py-14 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-6xl mb-3">🔒</div>
          <h1 className="text-3xl md:text-4xl font-black mb-3">سياسة الخصوصية</h1>
          <p className="text-gold-light text-base leading-relaxed max-w-2xl mx-auto">
            خصوصيتك مسؤوليّتنا. هذه السياسة توضّح أي بيانات نجمعها، ولماذا، وكيف نحميها.
          </p>
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur border border-white/20 rounded-full px-5 py-2.5 text-sm mt-5">
            <span>✓</span><span>متوافقون مع نظام حماية البيانات الشخصية (PDPL)</span>
          </div>
          <p className="text-gray-400 text-sm mt-4">آخر تحديث: محرّم ١٤٤٧ هـ</p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-5">
        <Card><CardBody>
          <p className="text-sm text-gray-700 leading-relaxed">
            تلتزم <b>شركة صورة العقارية</b> بحماية بياناتك الشخصية وفق أحكام نظام حماية البيانات الشخصية السعودي (PDPL)
            الصادر بالمرسوم الملكي رقم (م/١٩) ولوائحه التنفيذية. باستخدامك المنصة فإنك تقرّ بمعرفتك بمحتوى هذه السياسة.
          </p>
        </CardBody></Card>

        <Section n="1" title="البيانات التي نجمعها">
          <p className="text-sm text-gray-700 leading-relaxed mb-3">نجمع البيانات الآتية بحسب نوع تفاعلك مع المنصة:</p>
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li><b>بيانات الحساب:</b> الاسم، البريد الإلكتروني، رقم الجوال، كلمة المرور (مشفّرة).</li>
            <li><b>بيانات التحقق (KYC):</b> رقم الهوية، صور الوثائق، رقم رخصة فال (للوسطاء).</li>
            <li><b>بيانات الدفع:</b> آخر ٤ أرقام من البطاقة، وسيلة الدفع، رقم الآيبان للتحويلات. <em>لا نخزّن أرقام البطاقات الكاملة.</em></li>
            <li><b>بيانات الاستخدام:</b> العقارات التي تشاهدها، عمليات البحث، المفضّلة، التنبيهات.</li>
            <li><b>بيانات تقنية:</b> عنوان IP، نوع الجهاز، المتصفح، نظام التشغيل، المدينة (تقريبية).</li>
            <li><b>بيانات الموقع:</b> فقط إذا منحتنا إذناً لاستخدام الموقع لتخصيص نتائج الخريطة.</li>
            <li><b>المراسلات:</b> الرسائل المرسلة عبر المنصة لأغراض الجودة والامتثال.</li>
          </ul>
        </Section>

        <Section n="2" title="أغراض المعالجة">
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li>تشغيل المنصة وتقديم الخدمات (الإعلانات، المزادات، الاشتراكات، الدردشة).</li>
            <li>التحقق من هوية الوسطاء العقاريين والامتثال لنظام «فال».</li>
            <li>إصدار الفواتير ومعالجة المدفوعات وحفظ السجلات المحاسبية.</li>
            <li>تحسين تجربة المستخدم عبر تحليل أنماط الاستخدام بشكل مجمّع.</li>
            <li>إرسال إشعارات التشغيل (تأكيد الطلبات، تذكيرات المزادات، تحديثات الحالة).</li>
            <li>مكافحة الاحتيال وحماية الحسابات والمعاملات.</li>
            <li>الالتزام بأي طلب نظامي صادر عن جهة قضائية أو رقابية مختصّة.</li>
          </ul>
        </Section>

        <Section n="3" title="الأساس النظامي للمعالجة">
          <p className="text-sm text-gray-700 leading-relaxed">
            نعتمد على أحد المسوّغات النظامية الآتية: (أ) موافقتك الصريحة، (ب) تنفيذ عقد الخدمة معك،
            (ج) الامتثال لأنظمة المملكة، (د) المصلحة المشروعة للمنصة دون الإخلال بحقوقك.
          </p>
        </Section>

        <Section n="4" title="مشاركة البيانات">
          <p className="text-sm text-gray-700 leading-relaxed mb-3">قد نشارك بياناتك مع جهات محدودة، فقط بقدر ما يخدم الأغراض المعلنة:</p>
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li><b>الجهات الحكومية:</b> الهيئة العامة للعقار (REGA)، وزارة العدل، الجهات الأمنية — حصراً عند طلب نظامي.</li>
            <li><b>مزوّدو الدفع المعتمدون:</b> Tap Payments / Moyasar / HyperPay / STC Pay — لمعالجة المدفوعات.</li>
            <li><b>مزوّدو البنية التحتية:</b> Hostinger، Supabase، Resend (إيميل)، Twilio (SMS).</li>
            <li><b>المسوّقون العقاريون:</b> اسم المهتمّ ووسيلة تواصله فقط، عند تقديم طلب صريح للتواصل بشأن عقار.</li>
            <li><b>المستشارون القانونيون والمحاسبون:</b> ضمن نطاق ضيّق وبموجب اتفاقيات سرّية.</li>
          </ul>
          <p className="text-sm text-gray-700 leading-relaxed mt-3">
            <b>لا نبيع بياناتك لأي طرف إعلاني خارجي</b>، ولا نشاركها مع شركات تسويق غير ذات علاقة.
          </p>
        </Section>

        <Section n="5" title="نقل البيانات خارج المملكة">
          <p className="text-sm text-gray-700 leading-relaxed">
            بياناتك الأساسية مخزَّنة على خوادم في المملكة العربية السعودية. بعض الخدمات الفنية (مثل الإيميل والـ SMS) قد تتطلّب
            نقلاً مؤقتاً للبيانات لخوادم خارجية ضمن ضوابط المعايير العالمية للحماية، وذلك تماشياً مع المادة (٢٩) من نظام PDPL.
          </p>
        </Section>

        <Section n="6" title="مدة الاحتفاظ">
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li>بيانات الحساب النشط: طوال فترة الاستخدام.</li>
            <li>الفواتير والسجلات المالية: ١٠ سنوات (التزاماً بنظام ضريبة القيمة المضافة).</li>
            <li>سجلات النشاط الأمني (login, IP): ٧ سنوات.</li>
            <li>محادثات المزادات: ٥ سنوات بعد انتهاء المزاد.</li>
            <li>بيانات الحسابات المحذوفة: تُفقَد فعلياً خلال ٣٠ يوماً ما عدا ما يلزم نظاماً.</li>
          </ul>
        </Section>

        <Section n="7" title="حقوقك وفق نظام PDPL">
          <p className="text-sm text-gray-700 leading-relaxed mb-3">يمنحك النظام السعودي الحقوق الآتية، وكلها قابلة للممارسة عبر بريد الخصوصية:</p>
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li><b>حق العلم:</b> معرفة طبيعة بياناتك المُخزَّنة وأغراض معالجتها.</li>
            <li><b>حق الوصول:</b> طلب نسخة من بياناتك بصيغة قابلة للقراءة.</li>
            <li><b>حق التصحيح:</b> تعديل أي بيانات غير صحيحة أو ناقصة.</li>
            <li><b>حق الحذف:</b> طلب حذف بياناتك (مع استثناء البيانات الإلزامية نظاماً).</li>
            <li><b>حق الاعتراض:</b> رفض معالجة بعض البيانات لأغراض غير ضرورية للخدمة.</li>
            <li><b>حق سحب الموافقة:</b> في أي وقت، دون أثر رجعي.</li>
            <li><b>حق نقل البيانات:</b> الحصول على نسخة بصيغة JSON/CSV لنقلها.</li>
          </ul>
        </Section>

        <Section n="8" title="الأمن وحماية البيانات">
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li>تشفير الاتصال HTTPS / TLS على جميع الصفحات.</li>
            <li>تشفير كلمات المرور بـ bcrypt مع salt فردي.</li>
            <li>ضوابط وصول صارمة (RBAC) على لوحة الإدارة + سجل تدقيق.</li>
            <li>نسخ احتياطية يومية مشفّرة.</li>
            <li>مراقبة الجلسات والتنبيه على محاولات تسجيل الدخول المشبوهة.</li>
            <li>إخطار المستخدم خلال ٧٢ ساعة عند أي خرق بيانات يؤثر عليه (المادة ٢٠ من PDPL).</li>
          </ul>
        </Section>

        <Section n="9" title="ملفات تعريف الارتباط (Cookies)">
          <p className="text-sm text-gray-700 leading-relaxed mb-3">نستخدم ملفات Cookies للأغراض الآتية:</p>
          <ul className="space-y-2 text-sm text-gray-700 leading-relaxed list-disc pr-5">
            <li><b>ضرورية:</b> الجلسة، تسجيل الدخول، تذكّر التفضيلات.</li>
            <li><b>تحليلية:</b> فهم استخدام الصفحات لتحسين التجربة (مجمَّعة بدون تعريف شخصي).</li>
            <li><b>وظيفية:</b> اللغة، إعدادات الخريطة، الفلاتر المحفوظة.</li>
          </ul>
          <p className="text-sm text-gray-700 leading-relaxed mt-3">يمكنك إدارة Cookies من إعدادات متصفحك في أي وقت.</p>
        </Section>

        <Section n="10" title="الأطفال والقاصرون">
          <p className="text-sm text-gray-700 leading-relaxed">
            المنصة مخصّصة لمن أعمارهم ١٨ سنة فأكثر. لا نجمع بيانات قاصرين عن قصد. إذا اكتشفنا حساباً لقاصر،
            سيُحذَف فوراً وتُبلَّغ بياناته للأسرة عند الإمكان.
          </p>
        </Section>

        <Section n="11" title="ممارسة حقوقك / تقديم شكوى">
          <p className="text-sm text-gray-700 leading-relaxed">
            لممارسة أي من حقوقك، أو تقديم شكوى تتعلق بالخصوصية، تواصل مع مسؤول حماية البيانات لدينا:
            <br />📧 <a href="mailto:pdpl@img.sa" className="text-navy hover:underline" dir="ltr">pdpl@img.sa</a> (مخصّص لمواضيع حماية البيانات)
            <br />📧 <a href="mailto:info@img.sa" className="text-navy hover:underline" dir="ltr">info@img.sa</a> (للاستفسارات العامة)
            <br />📞 <a href="tel:0114993666" className="text-navy hover:underline" dir="ltr">0114993666</a>
            <br />نلتزم بالرد خلال ٣٠ يوم عمل من تاريخ استلام الطلب. وفي حال لم تكن راضياً، يحق لك التواصل مع
            الهيئة السعودية للبيانات والذكاء الاصطناعي (سدايا).
          </p>
        </Section>

        <Section n="12" title="تحديثات السياسة">
          <p className="text-sm text-gray-700 leading-relaxed">
            قد نحدّث هذه السياسة لتعكس تطوّر خدماتنا أو الأنظمة. أي تعديل جوهري يُبلَّغ به المستخدمون عبر الإيميل
            قبل ١٥ يوماً من بدء التطبيق. راجع كذلك{' '}
            <Link to="/terms" className="text-navy hover:underline">الشروط والأحكام</Link>.
          </p>
        </Section>
      </div>
    </div>
  );
}

function Section({ n, title, children }: { n: string; title: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody>
        <h2 className="text-lg font-black text-navy-dark mb-3 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-8 h-8 bg-navy text-white rounded-lg text-sm">{n}</span>
          {title}
        </h2>
        {children}
      </CardBody>
    </Card>
  );
}
